// Currency helpers — HTG (storage) ⇄ USD (display).
// Single source of truth for the site-wide exchange rate (admin-configurable).
import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const DEFAULT_RATE = 147;
let cachedRate = DEFAULT_RATE;
let inflight = null;
const subscribers = new Set();

const notify = (rate) => {
  cachedRate = rate;
  subscribers.forEach((cb) => {
    try { cb(rate); } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[currency] subscriber callback failed:", e?.message); }
  });
};

/** Fetch the current rate from backend (singleton — won't double-fire). */
export async function fetchExchangeRate(force = false) {
  if (!force && cachedRate !== DEFAULT_RATE) return cachedRate;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await axios.get(`${API}/config/exchange-rate`);
      const r = Number(res.data?.rate) || DEFAULT_RATE;
      notify(r);
      return r;
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.debug("[currency] fetch failed, using cached:", e?.message);
      return cachedRate || DEFAULT_RATE;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** React hook: returns the current rate and re-renders when admin changes it. */
export function useExchangeRate() {
  const [rate, setRate] = useState(cachedRate);
  useEffect(() => {
    let mounted = true;
    const cb = (r) => { if (mounted) setRate(r); };
    subscribers.add(cb);
    fetchExchangeRate().then(cb);
    // Refresh every 60s so admin rate changes propagate without reload
    const t = setInterval(() => fetchExchangeRate(true), 60000);
    return () => { mounted = false; subscribers.delete(cb); clearInterval(t); };
  }, []);
  return rate;
}

/** Pure helpers — pass `rate` explicitly so server-rendered/legacy code can use them. */
export const htgToUsd = (htg, rate) => {
  const r = Number(rate) || DEFAULT_RATE;
  const v = Number(htg) || 0;
  return v / r;
};
export const usdToHtg = (usd, rate) => {
  const r = Number(rate) || DEFAULT_RATE;
  const v = Number(usd) || 0;
  return v * r;
};

/** Formatters */
export const formatUSD = (htg, rate, opts = {}) => {
  const usd = htgToUsd(htg, rate);
  const min = opts.minFraction ?? 2;
  const max = opts.maxFraction ?? 2;
  return `${usd.toLocaleString("en-US", { minimumFractionDigits: min, maximumFractionDigits: max })} $`;
};
export const formatHTG = (htg) => {
  const v = Number(htg) || 0;
  return `${v.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} G`;
};
/** Composite: "1.50 $ (≈ 220 G)" — useful when context matters. */
export const formatUSDWithHTG = (htg, rate) => {
  return `${formatUSD(htg, rate)} (≈ ${formatHTG(htg)})`;
};
